import { getLocalTimeZone } from '@internationalized/date';
import Button from '@/components/Button';
import DatePicker from '@/components/datepicker/DatePicker';
import { toDateValue } from '@/components/datepicker/mappers';
import ToggleSwitch from '@/components/ToggleSwitch';
import { PlanCode } from '@/models/Billing';
import PlanCodeSelectBox, { PlanCodeItem } from '@/modules/billing/components/PlanCodeSelectBox';

interface StoreBillingControlsPanelProps {
  billing: {
    enabled: boolean;
    canEnable: boolean;
    plan: PlanCode;
    startedAt: Date | null;
    country?: string | null;
    countryCode?: string | null;
    currencyCode?: string | null;
  };
  loading?: boolean;
  onBillingEnabledChange: (nextValue: boolean) => void;
  onPlanChange: (nextPlan: PlanCode) => void;
  onStartedAtChange: (nextStartedAt: Date | null) => void;
  onSave: () => Promise<void>;
}

const sectionClassName = 'rounded-2xl border border-slate-200 bg-white p-6 lg:p-8';

const sectionTitleClassName = 'text-lg font-semibold text-slate-900';

const StoreBillingControlsPanel = ({
  billing,
  loading,
  onBillingEnabledChange,
  onPlanChange,
  onStartedAtChange,
  onSave,
}: StoreBillingControlsPanelProps) => {
  return (
    <div className={`lg:col-span-2 ${sectionClassName}`}>
      <div className="mb-6 border-b border-slate-200 pb-4">
        <div className={sectionTitleClassName}>Billing controls</div>
      </div>
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <ToggleSwitch
            checked={billing.enabled}
            onChange={onBillingEnabledChange}
            labelText="Billing enabled"
            data-testid="admin-billing-enabled-switch"
          />
          {!billing.canEnable && (
            <div className="mt-3 text-sm text-amber-700">
              Set a billing country before enabling billing.
            </div>
          )}
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <PlanCodeSelectBox
            value={billing.plan}
            onSelect={(item: PlanCodeItem | null) => onPlanChange(item?.id || 'paid')}
            label="Plan"
            disabled={!billing.enabled}
            requireSelection
          />
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <DatePicker
            label="Subscription start date"
            value={toDateValue(billing.startedAt ?? undefined)}
            onChange={(value) => onStartedAtChange(value ? value.toDate(getLocalTimeZone()) : null)}
            isDisabled={!billing.enabled || billing.plan !== 'paid'}
          />
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 lg:col-span-1">
          <label className="mb-2 block text-sm font-medium text-gray-700">Billing cycle</label>
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700">
            Monthly
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 lg:col-span-1">
          <label className="mb-2 block text-sm font-medium text-gray-700">Country</label>
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700">
            {billing.country || billing.countryCode || 'Not available'}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 lg:col-span-1">
          <label className="mb-2 block text-sm font-medium text-gray-700">Currency</label>
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700">
            {billing.currencyCode || 'Not available'}
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button onClick={onSave} loading={loading} data-testid="admin-billing-save-button">
          Save Billing
        </Button>
      </div>
    </div>
  );
};

export default StoreBillingControlsPanel;
